<template lang="pug">
.financial-detail
  FincancialDetailChart(v-if="report.financeDetails" :financeDetails="report.financeDetails" :loan="selectedLoan")
  .overview
    .title {{ $t('financial.overview.title') }}
    .cards
      FinancialDetailItem(v-for="item in overviewItems" :item-value="item.value", :description="$t('financial.overview.' + item.id)")  
  .tables
    FinancialDetailTable(
      :title="$t('financial.costOfFinance.title')"
      :items="costOfFinanceData.map(item => ({ label: $t('financial.costOfFinance.' + item.id), value: item.value }))"
    )
    FinancialDetailTable(
      :title="$t('financial.financialGains.title')"
      :items="financialGainsData.map(item => ({ label: $t('financial.financialGains.' + item.id), value: item.value }))"
    )
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import FinancialDetailItem from "./FinancialDetailItem.vue";
import FinancialDetailTable from "./FinancialTable.vue";
import FincancialDetailChart from "./FinancialDetailChart.vue";
import { useLoan } from "@/store/loan";
import { useRecommendations } from "@/store/recommendations";
import { storeToRefs } from "pinia";
import { formatNumberToString } from "@/helpers";
import { LoanType } from "@/types";

export default defineComponent({
  components: {
    FinancialDetailItem,
    DataView,
    FinancialDetailTable,
    FincancialDetailChart,
  },
  setup() {
    const recommendationStore = useRecommendations();
    const loanStore = useLoan();
    const { report } = storeToRefs(recommendationStore);
    const { selectedLoan, selectedLoanOption } = storeToRefs(loanStore);

    const overviewItems = computed(() => [
      {
        value: formatNumberToString(report.value?.totalMeasureCost || 0),
        id: "totalMeasureCost",
      },
      {
        value: formatNumberToString(report.value?.estimatedTotalCost || 0),
        id: "estimatedTotalCost",
      },
      {
        value: formatNumberToString(
          report.value?.estimatedTotalCostPlusLoanInterest || 0
        ),
        id: "estimatedTotalCostPlusLoanInterest",
      },
      {
        value: formatNumberToString(report.value?.estimatedMonthlyCostInPeriod || 0),
        id: "estimatedMonthlyCostInPeriod",
      },
    ]);

    const costOfFinanceData = computed(() =>
      report.value?.loanDetails
        ? [
            {
              id: "financeMethod",
              value: report.value.loanDetails.name,
            },
            {
              id: "fixedNormalRate",
              value: formatNumberToString(report.value.loanDetails.apr / 100, {
                style: "percent",
                maximumFractionDigits: 2,
              }),
            },
            {
              id: "term",
              value: `${report.value.loanDetails.repaymentPeriodYrs} years`,
            },
            {
              id: "annualRepayments",
              value: formatNumberToString(report.value.loanDetails.estimatedYearlyCost),
            },
            {
              id: "monthlyRepayments",
              value: formatNumberToString(report.value.loanDetails.estimatedMonthlyCost),
            },
          ]
        : []
    );

    const financialGainsData = computed(() =>
      report.value
        ? [
            {
              id: "estimatedGrant",
              value: formatNumberToString(report.value.totalGrantCost),
            },
            {
              id: "discount",
              value: formatNumberToString(report.value.totalDiscountCost),
            },
            {
              id: "totalGrant",
              value: formatNumberToString(report.value.totalGrantAidDiscountCost),
            },
            {
              id: "annualSavingsInPeriod",
              value: formatNumberToString(
                report.value.savingsInPeriod.estimatedYearlySavings
              ),
            },
            {
              id: "monthlySavingsInPeriod",
              value: formatNumberToString(
                report.value.savingsInPeriod.estimatedMonthlySavings
              ),
            },
          ]
        : []
    );

    return {
      overviewItems,
      costOfFinanceData,
      financialGainsData,
      report,
      selectedLoan,
      formatNumberToString,
      LoanType,
      selectedLoanOption,
    };
  },
});
</script>

<style lang="scss" scoped>
.financial-detail {
  display: flex;
  flex-direction: column;
  gap: 3rem;

  .title {
    margin-bottom: 1rem;
    font-size: 1.15rem;
    font-weight: 500;
  }

  .overview {
    .title {
      text-transform: uppercase;
    }
    .cards {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin: 0.5rem 0;
      padding: 0;
      box-shadow: none;
    }
  }

  .tables {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    > div {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  }
}

@media screen and (max-width: 720px) {
  .financial-detail {
    .tables {
      flex-direction: column;
    }
  }
}
</style>
